import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';
import { html, sortObjectArrayByKey, consoleLogColor } from './helper-functions.js';

/** @typedef {{ dropdownId: string, value: string, text: string[] }} Option */
/** @typedef { Record<String, any>} SellingPlan */

const chevronSvg = /* html */`
  <svg
    fill="none"
    height="20"
    viewBox="0 0 20 20"
    width="20"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      clip-rule="evenodd"
      d="M8.591 6.487a.555.555 0 00-.766 0 .512.512 0 000 .74L10.692 10l-2.867 2.773a.512.512 0 000 .74.555.555 0 00.766 0l3.25-3.143a.512.512 0 000-.74l-3.25-3.143z"
      fill="currentColor"
      fill-rule="evenodd"
    />
  </svg>
`;


const css = /* css */`
:host {
  position: relative;
  z-index: auto;
  box-sizing: border-box;
  display: block;
  width: 220px;
}

.Dropdown__outer-container {
  width: max-content;
}

.content-container {
  position: relative;
  width: 175px;
}

.content-container .DropdownOption__button {
  position: relative;
  font-size: 16px;
  color: var(--color-primary);
  background-color: var(--color-background);
  padding: calc(var(--input-padding-y) / 1.5) var(--input-padding-x);
  border-radius: var(--style-border-radius-pills);
  border: var(--style-border-width-inputs) solid var(--color-foreground);
  width: 100%;
  cursor: pointer;
  user-select: none;
  line-height: 1;
  transition: color var(--animation-speed) var(--animation-easing), background-color var(--animation-speed) var(--animation-easing);

  &:hover {
    color: var(--color-primary-button-text);
    background-color: var(--color-primary);
  }
}

.content-container :not(.DropdownOption__button).DropdownOption:not(:last-child) {
  border-bottom: var(--style-border-width-inputs) solid var(--color-border);
}

ul {
  margin-block-start: 0;
  margin-block-end: 0;
  padding-inline-start: 0;
}

.Dropdown__options-container {
  display: none;
  flex-direction: column;
  position: absolute;
  z-index: 2;
  top: 115%;
  left: var(--drawer-content-block-padding)
  width: max-content;
  height: 220px;
  color: var(--color-primary-button-text);
  background-color: var(--color-foreground);
  padding: var(--drawer-content-block-padding);
  box-shadow: var(--shadow-popover);
  font-size: var(--font-size--sm);
  padding: 0;
  margin: 0;
  list-style: none;
  border-radius: var(--style-border-radius-xs);
  overflow-y: auto;
}

.Dropdown__options-container.active {
  display: flex;
}

ul li {
  width: max-content;
  font-size: var(--font-size--sm)
}

.DropdownOption {
  width: -webkit-fill-available;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--gap-xs);
  padding: var(--input-padding);
  cursor: pointer;
  user-select: none;
  text-wrap-mode: nowrap;

  &:hover {
    background-color: var(--color-input-hover-background);
  }
}

.DropdownOption__radio-outer-circle {
  background-color: transparent;
  width: var(--icon-size-md);
  height: var(--icon-size-md);
  min-width: var(--icon-size-md);
  min-height: var(--icon-size-md);
  border-radius: var(--style-border-radius-50);
  border: var(--style-border-width-inputs) solid var(--color-foreground);
  display: flex;
  justify-content: center;
  align-items: center;
}

.DropdownOption__radio-inner-circle {
  background-color: transparent;
  width: var(--icon-size-xs);
  height: var(--icon-size-xs);
  min-width: var(--icon-size-xs);
  min-height: var(--icon-size-xs);
  border-radius: var(--style-border-radius-50);
}

.DropdownOption__radio-outer-circle {
  background-color: var(--color-foreground);
  border: var(--style-border-width-inputs) solid var(--color-input-border);
}

.DropdownOption.is-active .DropdownOption__radio-inner-circle {
  background-color: var(--color-secondary-button-background);
}

.DropdownOption:not(.is-active):hover .DropdownOption__radio-inner-circle {
  background-color: var(--color-selected-variant-hover-background);
}

.content-container .DropdownOption__button.DropdownOption__single-option {
  cursor: default;

  &:hover {
    color: var(--color-primary);
    background-color: var(--color-background);
  }
}

.DropdownOption__icon {
  position: absolute;
  right: 0;
  bottom: 50%;
  transform: translateY(50%) rotate(90deg);

  & svg {
    transform: translateY(50%) rotate(0deg);
    transition: transform var(--animation-speed) var(--animation-easing);
  }

  &.active svg {
    transform: translateY(50%) rotate(-180deg);
  }
}
`;



class CartItemFrequencyDropdown extends Component {    
  static observedAttributes = ['data-cart-item-key'];
  /** @type {any[]} */ allProducts;
  /** @type {SellingPlan|undefined} */ selectedSellingPlan;
  /** @type {SellingPlan[]} */ sellingPlans;
  
  constructor() {
    super();
    this.allProducts = [];
    this.shadow = this.attachShadow({ mode: "open" });
    this.render();

    this.shadow.addEventListener('wheel', (e) => {
      e.stopPropagation();
    }, { passive: true });
  }


  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    this.parentCartEl = this.closest('cart-items-component');
    this.cartSectionId = this.parentCartEl?.dataset.sectionId;
    this.parentCartEl?.addEventListener('click', this.handleCartDrawerClick.bind(this));
    document.addEventListener('products-data-loaded', this.loadProductData.bind(this))
    this.loadProductData();
  }


  disconnectedCallback() {
    super.disconnectedCallback();
    this.parentCartEl?.removeEventListener('click', this.handleCartDrawerClick.bind(this));
    document.removeEventListener('products-data-loaded', this.loadProductData.bind(this))
  }


  loadProductData() {
    const script = document.querySelector('#all-products-data');
    const jsonText = script?.textContent;
    if (!jsonText) return;
    this.allProducts = JSON.parse(jsonText);
    this.render();
  }


  /** @param {string} name @param {any} oldValue @param {any} newValue */
  attributeChangedCallback(name, oldValue, newValue) {
    this.render();
  }


  handleCartDrawerClick(/** @type {PointerEvent} */e) {
    /** @type {HTMLElement | null} */
    // @ts-ignore
    const target = e.target;
    if (!target) return;

    if (this.optionsContainerEl?.classList.contains('active') && !this.shadow.contains(target) && target !== this) {
      this.optionsContainerEl.classList.remove('active');
      this.iconEl?.classList.remove('active');
      this.style.setProperty('z-index', 'auto');
    }
  }


  async fetchSellingPlans() {
    this.productHandle = this.getAttribute('data-product-handle');
    try {
      this.productData = this.allProducts.find((product) => product.handle === this.productHandle);

      const sellingPlans = this.productData?.selling_plan_groups?.[0]?.selling_plans
        ?.filter((/** @type {Record<String, any>} */ plan) => !!plan.id) ?? [];
      return sortObjectArrayByKey(sellingPlans, 'order_interval_frequency');
    } catch (error) {
      console.error(`Failed to fetch product data for ${this.productHandle}: error`, error);
      return [];
    }
  }


  async fetchCartItems() {
    try {
      const response = await fetch(`/cart.js`);
      const cartData = await response.json();
      return cartData.items;
    } catch (error) {
      console.error(`Failed to fetch cart items: error`, error);
      return null;
    } 
  }


  /** @param {HTMLElement} dropdownOption */
  handleDropdownOptionClick(dropdownOption) {
    this.optionsContainerEl?.classList.remove('active');
    this.iconEl?.classList.remove('active');

    const dataset = dropdownOption.dataset;
    const cartItemKey = dataset.cartItemKey;
    const sellingPlanId = dataset.sellingPlanId ? Number(dataset.sellingPlanId) : undefined;

    this.changeSellingPlan(cartItemKey, sellingPlanId, this.cartSectionId);  
  }


  /**
   * @param {string | undefined} cartItemKey
   * @param {number | undefined} sellingPlanId
   * @param {string| undefined} sectionsToUpdate */
  async changeSellingPlan(cartItemKey, sellingPlanId, sectionsToUpdate) {

    if (!cartItemKey || !sellingPlanId || !sectionsToUpdate) {
      console.error('missing values, returning', { cartItemKey, sellingPlanId, sectionsToUpdate }); 
      return;
    }

    const cartItems = await this.fetchCartItems();
    const currentCartItem = cartItems.find((/** @type {Record<String, any>} */ item) => item.key === cartItemKey);
    const quantity = currentCartItem.quantity;

    const changeRequestBody = {
      id: cartItemKey,
      selling_plan: sellingPlanId,
      quantity,
      sections: sectionsToUpdate,
    };
    
    const changeResponse = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(changeRequestBody),
    });
    
    if (!changeResponse.ok) {
      const errorText = await changeResponse.text();
      console.error('Error response from cart/change.js:', errorText);
      if (errorText.includes('Cannot apply selling plan to variant')) {
        alert('This product does not support subscriptions. Please contact customer support for assistance.');
      } else {
        alert('There was an error changing this subscription. Please try again later.');
      }
      throw new Error(`HTTP error! status: ${changeResponse.status}`);
    }
    
    const changeJson = await changeResponse.json();

    if (changeJson.sections) {
      Object.entries(changeJson.sections).forEach(([key, value]) => morphSection(key, value));
    }

    if (!this.cartSectionId) return;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.cartSectionId, {
        itemCount: quantity,
        source: 'cart-items-component',
        sections: changeJson.sections || {},
      })
    );
  }


  handleToggleShowOptions() {
    if (this.optionsContainerEl?.classList.contains('active')) {
      this.optionsContainerEl.classList.remove('active');
      this.iconEl?.classList.remove('active');
      this.style.setProperty('z-index', 'auto');
    } else {
      this.optionsContainerEl?.classList.add('active');
      this.iconEl?.classList.add('active');
      this.style.setProperty('z-index', '1');
    }
  }


  
  async render() {
    const productHandle = this.getAttribute('data-product-handle');
    const variantId = this.getAttribute('data-variant-id');
    const cartItemKey = this.getAttribute('data-cart-item-key');

    if (!productHandle || !variantId || !cartItemKey) {
      console.error('missing values, returning', { productHandle, variantId, cartItemKey });
      return;
    }

    const cartItems = await this.fetchCartItems();
    const sellingPlans = await this.fetchSellingPlans();

    const currentCartItem = cartItems.find((/** @type {any} */ item) => item.key === cartItemKey); 

    const sellingPlanId = currentCartItem?.selling_plan_allocation.selling_plan.id;
    const selectedSellingPlan = sellingPlans?.find(plan => plan.id === Number(sellingPlanId));

    if (sellingPlans.length === 1 && sellingPlans[0].name.startsWith('1 month subscription')) {
      this.renderSingleSellingPlan();
      return;
    }
    if (this.shadow.querySelector('.DropdownOption__single-option')) {
      this.shadow.innerHTML = '';
    }

    const options = sellingPlans
      .map((plan) => `
        <li data-selling-plan-id="${plan.id}" data-cart-item-key="${cartItemKey}" class="DropdownOption">
          <div class="DropdownOption__radio-outer-circle">
            <div class="DropdownOption__radio-inner-circle"></div>
          </div>
          <span>${plan.name}</span>
        </li>
      `);

    this.selectedSellingPlanName = selectedSellingPlan?.name || '';

    const div = document.createElement('div');
    div.className = 'Dropdown__outer-container';
    div.innerHTML = /* html */`
      <style>${css}</style>

      <div class="content-container">
        <div class="DropdownOption__button">
          ${selectedSellingPlan?.name ?? ''}
          <span class="DropdownOption__icon">${chevronSvg}</span>
        </div>
        <ul class="Dropdown__options-container">${options.join('')}</ul> 
      </div>
    `;

    const rootEl = this.shadow.querySelector('.Dropdown__outer-container');
    if (!rootEl) {
      this.shadow.appendChild(div);
    } else {
      rootEl.replaceWith(div);
    }

    /** @type {HTMLElement[] | null} */
    // @ts-ignore
    const dropdownOptions = [...this.shadow.querySelectorAll('.DropdownOption')];
    /** @type {HTMLElement | null} */
    dropdownOptions?.forEach((dropdownOption) => dropdownOption.addEventListener('click', () => {
      this.handleDropdownOptionClick(dropdownOption);
    }));

    dropdownOptions?.forEach((option) => {
      if (option.dataset.sellingPlanId == sellingPlanId) {
        option.classList.add('is-active');
      } else if (option.classList.contains('is-active')) {
        option.classList.remove('is-active');
      }
    });

    this.buttonEl = this.shadow.querySelector('.DropdownOption__button');
    this.optionsContainerEl = this.shadow.querySelector('.Dropdown__options-container');
    this.iconEl = this.shadow.querySelector('.DropdownOption__icon');
    this.buttonEl?.addEventListener('click', this.handleToggleShowOptions.bind(this));

    this.dispatchEvent(new CustomEvent('frequency-dropdown-updated', {
      bubbles: true,
      detail: { selectedPlanName: selectedSellingPlan?.name || '' }
    }));
  }


  renderSingleSellingPlan() {
    if (this.shadow.querySelector('.DropdownOption__single-option')) {
      return;
    }
    const div = document.createElement('div');
    div.className = 'content-container';
    div.innerHTML = /* html */`
      <style>${css}</style>
      <div class="DropdownOption__button DropdownOption__single-option">1 month subscription</div>
    `;

    const rootEl = this.shadow.querySelector('.Dropdown__outer-container');
    if (!rootEl) {
      this.shadow.appendChild(div);
    } else {
      rootEl.replaceWith(div);
    }

    this.dispatchEvent(new CustomEvent('frequency-dropdown-updated', {
      bubbles: true,
      detail: { selectedPlanName: '1 month subscription' }
    }));
  }
}

if (!customElements.get('cart-item-frequency-dropdown')) {
  customElements.define('cart-item-frequency-dropdown', CartItemFrequencyDropdown);
}